import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
	type MorePage,
	mergeUsedByGroups,
	USED_BY_PANEL_KINDS,
	type UsedByGroup,
	type UsedBySubject
} from "~/components/usedBy/usedByHelpers";
import { RecordKind } from "~/gql/graphqlBase.generated";
import {
	type GetCategoryUsedByQuery,
	type GetCustomerTypeUsedByQuery,
	type GetPriceGroupUsedByQuery
} from "~/gql/queries/usedBy.generated";
import { useSnacks } from "~/providers/SnackProvider";
import {
	useGetCategoryUsedByQuery,
	useGetCustomerTypeUsedByQuery,
	useGetPriceGroupUsedByQuery,
	useLazyGetCategoryUsedByQuery,
	useLazyGetCustomerTypeUsedByQuery,
	useLazyGetPriceGroupUsedByQuery
} from "~/services/endpoints/apiEndpoints";

const PAGE_SIZE = 50;

export interface UseUsedByReturn {
	groups: UsedByGroup[];
	isError: boolean;
	isLoading: boolean;
	loadingMoreKind: null | RecordKind;
	refetch: () => void;
	showMore: (kind: RecordKind) => Promise<void>;
	total: number;
}

type UsedByQuery = GetCategoryUsedByQuery | GetCustomerTypeUsedByQuery | GetPriceGroupUsedByQuery;

const pickUsedBy = (data: UsedByQuery | undefined) => {
	if (!data) return null;
	const connection =
		"categories" in data
			? data.categories
			: "customerTypes" in data
				? data.customerTypes
				: "priceGroups" in data
					? data.priceGroups
					: null;
	return connection?.nodes?.[0]?.usedBy ?? null;
};

const useUsedBy = ({ id, kind }: UsedBySubject): UseUsedByReturn => {
	const { t } = useTranslation("common");
	const [openSnack] = useSnacks();
	const isCategory = kind === RecordKind.Category;
	const isCustomerType = kind === RecordKind.CustomerType;
	const isPriceGroup = kind === RecordKind.PriceGroup;
	const firstPage = { first: PAGE_SIZE, limit: 1, offset: 0 };
	const categoryPage = { ...firstPage, where: { id: { eq: id } } };
	const customerTypePage = { ...firstPage, where: { customerTypeId: { eq: id } } };
	const priceGroupPage = { ...firstPage, where: { priceId: { eq: id } } };
	const options = { refetchOnMountOrArgChange: true };
	const categoryQuery = useGetCategoryUsedByQuery(categoryPage, { ...options, skip: !isCategory });
	const customerTypeQuery = useGetCustomerTypeUsedByQuery(customerTypePage, { ...options, skip: !isCustomerType });
	const priceGroupQuery = useGetPriceGroupUsedByQuery(priceGroupPage, {
		...options,
		skip: !isPriceGroup
	});
	const [fetchCategoryPage] = useLazyGetCategoryUsedByQuery();
	const [fetchCustomerTypePage] = useLazyGetCustomerTypeUsedByQuery();
	const [fetchPriceGroupPage] = useLazyGetPriceGroupUsedByQuery();
	const [morePages, setMorePages] = useState<Partial<Record<RecordKind, MorePage>>>({});
	const [loadingMoreKind, setLoadingMoreKind] = useState<null | RecordKind>(null);

	const queriesByKind = {
		[RecordKind.Category]: categoryQuery,
		[RecordKind.CustomerType]: customerTypeQuery,
		[RecordKind.PriceGroup]: priceGroupQuery
	} satisfies Record<
		(typeof USED_BY_PANEL_KINDS)[number],
		typeof categoryQuery | typeof customerTypeQuery | typeof priceGroupQuery
	>;
	const query = queriesByKind[kind as keyof typeof queriesByKind] ?? priceGroupQuery;
	const usedBy = pickUsedBy(query.data);
	const groups = mergeUsedByGroups(usedBy?.groups ?? [], morePages);

	const showMore = async (groupKind: RecordKind) => {
		const loaded = groups.find((group) => group.kind === groupKind)?.refs.length ?? 0;
		const paging = { kind: groupKind, offset: loaded };
		setLoadingMoreKind(groupKind);
		try {
			const moreUsedByResponse = await (isCategory
				? fetchCategoryPage({ ...categoryPage, ...paging })
				: isCustomerType
					? fetchCustomerTypePage({ ...customerTypePage, ...paging })
					: fetchPriceGroupPage({ ...priceGroupPage, ...paging }));
			const page = pickUsedBy(moreUsedByResponse.data)?.groups.find((group) => group.kind === groupKind);
			if (!page) {
				openSnack(t("usedBy.moreError"), "error");
				return;
			}
			setMorePages((current) => ({
				...current,
				[groupKind]: { hasMore: page.hasMore, refs: [...(current[groupKind]?.refs ?? []), ...page.refs] }
			}));
		} finally {
			setLoadingMoreKind(null);
		}
	};

	return {
		groups,
		isError: query.isError || (!query.isLoading && !query.isUninitialized && !usedBy),
		isLoading: query.isLoading,
		loadingMoreKind,
		refetch: query.refetch,
		showMore,
		total: usedBy?.total ?? 0
	};
};

export default useUsedBy;
